import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, Smartphone } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { MenuItem } from '@/components/ui/Popover';
import {
  deriveClinicPayDisplayStatus,
  useClinicPayStatusLocal,
  useRequestClinioPayLink,
} from '@/hooks/useClinicPay';
import { useSendInvoiceEmail } from '@/hooks/useInvoices';
import { useSendSms, useSmsClinic } from '@/hooks/useSms';
import { hasValidPatientEmail } from '@/lib/calendarPaymentChoice';
import {
  apiErrorCode,
  hasPatientPhone,
  patientAcceptsSms,
  payLinkSmsErrorKey,
} from '@/lib/payLinkDelivery';
import { UI_MUTED } from '@/lib/typography';

type Channel = 'email' | 'sms';

export function SendClinioPayLink({
  clinicId,
  invoiceId,
  invoiceNumber,
  patientId,
  email,
  phone,
  patientName,
  smsConsent,
  consentLoaded,
  menuRows = false,
}: {
  clinicId: string;
  invoiceId: string;
  invoiceNumber: string | null;
  patientId: string;
  email: string | null;
  phone: string | null;
  patientName: string;
  smsConsent: unknown;
  consentLoaded: boolean;
  /** Rows for the Send betalingslink/faktura menu. The handlers stay the same. */
  menuRows?: boolean;
}) {
  const { t } = useTranslation();
  const requestPayLink = useRequestClinioPayLink();
  const sendEmail = useSendInvoiceEmail();
  const sendSms = useSendSms();
  const { data: payStatus, isFetched: payFetched } = useClinicPayStatusLocal(clinicId);
  const { data: smsClinic, isFetched: smsFetched } = useSmsClinic(clinicId);
  const [channel, setChannel] = useState<Channel | null>(null);

  const payActive = deriveClinicPayDisplayStatus(payStatus) === 'active';
  const emailOk = hasValidPatientEmail(email);
  const phoneOk = hasPatientPhone(phone);
  const smsEnabled = !smsFetched || smsClinic?.settings.enabled !== false;
  const consentOk = !consentLoaded || patientAcceptsSms(smsConsent);
  const busy = channel !== null;

  async function ensurePayUrl(): Promise<string> {
    const pay = await requestPayLink.mutateAsync(invoiceId);
    return pay.pay_url ?? `${window.location.origin}/pay-invoice/${pay.pay_token}`;
  }

  async function sendByEmail() {
    if (!emailOk || !email) return;
    setChannel('email');
    try {
      const url = await ensurePayUrl();
      await sendEmail.mutateAsync({
        invoice_id: invoiceId,
        recipient: email.trim(),
        message: t('payments.pay_link_email_message', { url }),
      });
      toast.success(t('payments.pay_link_email_sent', { recipient: email.trim() }));
    } catch {
      toast.error(t('payments.pay_link_email_failed'));
    } finally {
      setChannel(null);
    }
  }

  async function sendBySms() {
    if (!phoneOk || !phone) return;
    setChannel('sms');
    try {
      const url = await ensurePayUrl();
      const number = invoiceNumber?.trim() || '—';
      await sendSms.mutateAsync({
        clinic_id: clinicId,
        patient_id: patientId,
        subscriber_identifier: phone.trim(),
        body: t('payments.pay_link_sms_body', { number, url }),
        trigger: 'manual',
        patient_name: patientName,
      });
      toast.success(t('payments.pay_link_sms_sent', { phone: phone.trim() }));
    } catch (error) {
      const key = payLinkSmsErrorKey(apiErrorCode(error));
      toast.error(key ? t(key) : t('payments.pay_link_sms_failed'));
    } finally {
      setChannel(null);
    }
  }

  const emailLabel = channel === 'email' ? t('sms.sending') : t('payments.send_row_link_email');
  const smsLabel = channel === 'sms' ? t('sms.sending') : t('payments.send_row_link_sms');
  const linkHint = t('payments.pay_link_send_hint');
  const emailDisabled = busy || !payActive || !emailOk;
  const smsDisabled = busy || !payActive || !phoneOk || !smsEnabled || !consentOk;

  if (menuRows) {
    return (
      <>
        <MenuItem
          icon={
            channel === 'email' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" aria-hidden />
            )
          }
          disabled={emailDisabled}
          data-send-pay-link-email=""
          description={
            <>
              <span className="block">{linkHint}</span>
              {!emailOk ? <span className="block">{t('payments.pay_link_email_missing')}</span> : null}
            </>
          }
          onClick={() => {
            void sendByEmail();
          }}
        >
          {emailLabel}
        </MenuItem>
        <MenuItem
          icon={
            channel === 'sms' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Smartphone className="h-4 w-4" aria-hidden />
            )
          }
          disabled={smsDisabled}
          data-send-pay-link-sms=""
          description={
            <>
              <span className="block">{linkHint}</span>
              {!phoneOk ? <span className="block">{t('payments.pay_link_phone_missing')}</span> : null}
              {phoneOk && smsFetched && !smsEnabled ? (
                <span className="block">{t('payments.pay_link_sms_disabled')}</span>
              ) : null}
              {phoneOk && smsEnabled && consentLoaded && !patientAcceptsSms(smsConsent) ? (
                <span className="block">{t('payments.pay_link_sms_no_consent')}</span>
              ) : null}
            </>
          }
          onClick={() => {
            void sendBySms();
          }}
        >
          {smsLabel}
        </MenuItem>
      </>
    );
  }

  return (
    <section className="space-y-2" data-send-pay-link="">
      <h6 className="text-sm font-medium text-ds-text">
        {t('payments.pay_link_send_title')}
      </h6>
      <p className={UI_MUTED}>{t('payments.pay_link_send_hint')}</p>
      {payFetched && !payActive ? (
        <p className="rounded-md bg-ds-amber-tint px-3 py-2 text-sm text-ds-amber-ink">
          {t('payments.pay_not_configured')}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          data-send-pay-link-email=""
          disabled={busy || !payActive || !emailOk}
          onClick={() => {
            void sendByEmail();
          }}
        >
          {channel === 'email' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Mail className="h-4 w-4" />
          )}
          <span className="ml-1">{emailLabel}</span>
        </Button>
        <Button
          type="button"
          variant="secondary"
          data-send-pay-link-sms=""
          disabled={busy || !payActive || !phoneOk || !smsEnabled || !consentOk}
          onClick={() => {
            void sendBySms();
          }}
        >
          {channel === 'sms' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Smartphone className="h-4 w-4" />
          )}
          <span className="ml-1">{smsLabel}</span>
        </Button>
      </div>
      <p className={UI_MUTED} data-send-pay-link-email-target="">
        {emailOk && email ? email.trim() : t('payments.pay_link_email_missing')}
      </p>
      <p className={UI_MUTED} data-send-pay-link-sms-target="">
        {phoneOk && phone ? phone.trim() : t('payments.pay_link_phone_missing')}
        {phoneOk && smsFetched && !smsEnabled ? ` · ${t('payments.pay_link_sms_disabled')}` : ''}
        {phoneOk && smsEnabled && consentLoaded && !patientAcceptsSms(smsConsent)
          ? ` · ${t('payments.pay_link_sms_no_consent')}`
          : ''}
      </p>
    </section>
  );
}
